/**
 * The way back, on every page that has one.
 *
 * Inline above the page content rather than floating, because a floating
 * control covers data on a roster of six hundred rows and has to be dodged;
 * inline it occupies its own line and never hides anything.
 *
 * It navigates by history rather than to a computed parent, which is the less
 * obvious of the two choices and the right one here. A gradebook is reached
 * from a section list, from a student profile, and from a teacher's home, and
 * a parent-route rule sends everyone to whichever of those was guessed at
 * build time. History sends each person where they actually came from.
 *
 * The exception is arriving cold — a bookmark, a link from an email, a reload
 * on a deep page. There is nothing useful behind those, and a back control
 * that lands someone on the sign-in screen or outside the application is worse
 * than none. So the label falls back to the section the route belongs to, and
 * the control navigates there instead.
 */

import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconChevronDown } from './icons'

/**
 * The sections a route can fall back to.
 *
 * Ordered longest-prefix first so `/students/success` resolves to the Student
 * Success Center rather than to Students.
 */
const SECTIONS: { prefix: string; label: string; to: string }[] = [
  { prefix: '/students/success', label: 'Student Success', to: '/students/success' },
  { prefix: '/students', label: 'Students', to: '/students' },
  { prefix: '/admissions', label: 'Admissions', to: '/admissions' },
  { prefix: '/forms', label: 'Forms', to: '/forms' },
  { prefix: '/accounts', label: 'Finance', to: '/accounts' },
  { prefix: '/work', label: 'My Work', to: '/work' },
  { prefix: '/special-programs', label: 'Special Programs', to: '/special-programs' },
  { prefix: '/growth', label: 'Professional Growth', to: '/growth' },
  { prefix: '/academics', label: 'Academics', to: '/academics' },
  { prefix: '/attendance', label: 'Attendance', to: '/attendance' },
  { prefix: '/grades', label: 'Grades', to: '/grades' },
  { prefix: '/scheduling', label: 'Scheduling', to: '/scheduling' },
  { prefix: '/reports', label: 'Reports', to: '/reports' },
  { prefix: '/operations', label: 'Operations', to: '/operations' },
  { prefix: '/facilities', label: 'Facilities', to: '/facilities' },
  { prefix: '/administration', label: 'Administration', to: '/administration' },
  { prefix: '/feedback', label: 'Feedback Lab', to: '/feedback' },
  { prefix: '/platform', label: 'Platform', to: '/platform' },
  { prefix: '/classes', label: 'Classes', to: '/classes' },
  { prefix: '/assignments', label: 'Assignments', to: '/assignments' },
  { prefix: '/state-reporting', label: 'State Reporting', to: '/state-reporting' },
  { prefix: '/documents', label: 'Documents', to: '/documents' },
  { prefix: '/calendar', label: 'Calendar', to: '/calendar' },
  { prefix: '/messages', label: 'Messages', to: '/messages' },
  { prefix: '/staff', label: 'Staff', to: '/staff' },
  { prefix: '/transportation', label: 'Transportation', to: '/transportation' },
  { prefix: '/dismissal', label: 'Dismissal', to: '/dismissal' },
  { prefix: '/library', label: 'Library', to: '/library' },
  { prefix: '/assets', label: 'Assets', to: '/assets' },
  { prefix: '/purchasing', label: 'Purchasing', to: '/purchasing' },
  { prefix: '/fees', label: 'Fees', to: '/fees' },
  { prefix: '/health-office', label: 'Health Office', to: '/health-office' },
  { prefix: '/health', label: 'Health', to: '/health' },
  { prefix: '/discipline', label: 'Discipline', to: '/discipline' },
  { prefix: '/activities', label: 'Activities', to: '/activities' },
  { prefix: '/evaluations', label: 'Evaluations', to: '/evaluations' },
  { prefix: '/absences', label: 'Absences', to: '/absences' },
  { prefix: '/print-center', label: 'Print Center', to: '/print-center' },
]

export function BackLink() {
  const navigate = useNavigate()
  const location = useLocation()

  const section = useMemo(() => {
    const match = SECTIONS.find(
      (entry) => location.pathname === entry.prefix || location.pathname.startsWith(`${entry.prefix}/`),
    )
    // Already at the top of a section: the way back is the dashboard.
    if (match && location.pathname === match.to) {
      return { label: 'Dashboard', to: '/' }
    }
    return match ?? { label: 'Dashboard', to: '/' }
  }, [location.pathname])

  /**
   * Whether this tab has somewhere to go back to inside the application.
   *
   * React Router keeps its position in its own history stack in
   * `window.history.state.idx`: zero on a cold load, counting up as you
   * navigate. (It is not on `location` — reading it there always found
   * nothing, so Back always fell through to the section or the dashboard.)
   * `window.history.length` is not a substitute: it counts entries from
   * before this application was opened, so a fresh tab that followed a link
   * here would be sent off the site.
   */
  const canGoBack = (((typeof window !== 'undefined' ? window.history.state : null) as { idx?: number } | null)?.idx ?? 0) > 0

  const goBack = useCallback(() => {
    if (canGoBack) navigate(-1)
    else navigate(section.to)
  }, [canGoBack, navigate, section.to])

  // Nothing above the dashboard.
  if (location.pathname === '/') return null

  return (
    <button type="button" className="back-link" onClick={goBack}>
      <IconChevronDown className="back-link__caret" aria-hidden="true" />
      <span>{canGoBack ? 'Back' : `Back to ${section.label}`}</span>
    </button>
  )
}
